/* Emissões → Lançamento manual: POST /lancamentos calcula e grava documentos + cat_* + emissoes. */

import { api } from '../../core/api';
import { catBySlug, obraNome } from '../../core/catalogo';
import { state } from '../../core/estado';
import { dataBR, esc, lista, nf, num } from '../../core/formato';
import type { Campo, Lancamento, ResultadoLancamento } from '../../tipos/api';
import { campoHtml, lerCampos, opcoesCategorias, temPadrao, validaCampos } from '../../ui/campos';
import { carregar } from '../../ui/carregar';
import { emptyTable, escLabel, loading, pillEscopo, tableFoot } from '../../ui/componentes';
import { ic } from '../../ui/dom';
import { toast } from '../../ui/toast';
import { render } from '../../roteador';
import type { Pagina } from '../pagina';

const DOC_TOPO = ['data', 'descricao', 'id_unidade'];
const DOC_RODAPE = ['cnpj_fornecedor', 'link_referencia'];

function tabelaLancamentos(d: Lancamento[]): string {
  const l = lista<Lancamento>(d);
  if (!l.length) return emptyTable(['Data', 'Categoria', 'Obra', 'tCO₂e'], 'Nenhum lançamento neste ano ainda.');
  return `<div class="table-wrap"><table class="table">
    <thead><tr><th>Data</th><th>Categoria</th><th>Obra</th><th class="r">tCO₂e</th></tr></thead>
    <tbody>${l.map((x) => `<tr><td class="num">${esc(dataBR(x.data))}</td>
      <td><div style="font-weight:600">${esc(x.categoria)}</div><div style="margin-top:4px">${pillEscopo(x.escopo)}</div>
        <small style="color:var(--muted)">${esc(x.descricao)}</small></td>
      <td>${esc(x.obra)}</td>
      <td class="num r"><b>${nf(x.total, 3)}</b></td></tr>`).join('')}
    </tbody></table>${tableFoot(l.length)}</div>`;
}

const carregarRecentes = (tk: number) =>
  carregar(tk, [{
    id: 'lancRecentes',
    // Só o que foi lançado na plataforma nova (o histórico do legado não entra nesta lista).
    busca: () => api<Lancamento[]>('GET', '/lancamentos', { params: { ano: state.f.ano, limite: 8, origem: 'backend' } }),
    html: tabelaLancamentos,
  }]);

export const lancar: Pagina = {
  render() {
    const c = catBySlug(state.lc);
    state.lc = c.slug;
    const v = state.lcVals;
    const campo = (nome: string) => c.campos.find((x) => x.nome === nome) as Campo;
    const especificos = c.campos.filter((x) => ![...DOC_TOPO, ...DOC_RODAPE].includes(x.nome));
    // A unidade (opções) fica junto da quantidade mesmo quando opcional.
    const principal = (x: Campo) => x.obrigatorio || x.tipo === 'opcoes';
    const obrigatorios = especificos.filter(principal);
    const opcionais = especificos.filter((x) => !principal(x));
    return `
  <h1 class="page">Lançamento manual</h1>
  <p class="sub">Registre uma emissão pontual. A categoria define o escopo e os campos pedidos; o cálculo em tCO₂e é feito pelo motor da ZNIT e gravado no inventário assim que você salva.</p>
  <div class="grid g2" style="align-items:start">
    <div class="card">
      <h2 class="sec">Novo lançamento</h2>
      <div class="field"><label for="lcCategoria">Categoria</label>
        <select class="inp" id="lcCategoria" data-muda="categoria">${opcoesCategorias(c.slug)}</select>
        <div class="hint">${esc(c.descricao)}</div></div>
      <div class="grid g2" style="gap:0 14px">
        <div class="field"><label>Escopo</label><input class="inp" value="${escLabel(c.escopo)}" disabled></div>
        ${campoHtml(campo('data'), v)}
      </div>
      ${campoHtml(campo('descricao'), v)}
      ${campoHtml(campo('id_unidade'), v)}
      <div class="grid g2" style="gap:0 14px">${obrigatorios.map((x) => campoHtml(x, v)).join('')}</div>
      <div class="grid g2" style="gap:0 14px">${DOC_RODAPE.map((n) => campoHtml(campo(n), v)).join('')}</div>
      ${opcionais.length ? `<details class="adv"><summary>Campos opcionais</summary><div class="grid g2" style="gap:0 14px">${opcionais.map((x) => campoHtml(x, v)).join('')}</div></details>` : ''}
      <button class="btn btn-primary" id="lcSalvar" data-clique="salvar">${ic('i-save')} Salvar lançamento</button>
    </div>
    <div class="card">
      <div class="between" style="margin-bottom:14px"><h2 class="sec" style="margin:0">Últimos lançamentos</h2><span class="pill grey">${esc(state.f.ano)}</span></div>
      <div id="lancRecentes">${loading()}</div>
    </div>
  </div>`;
  },

  carregar: carregarRecentes,

  acoes: {
    /** Troca a categoria mantendo data, descrição, obra e fornecedor já digitados. */
    categoria(el) {
      const atuais = lerCampos(catBySlug(state.lc));
      state.lcVals = Object.fromEntries([...DOC_TOPO, ...DOC_RODAPE].filter((k) => atuais[k]).map((k) => [k, atuais[k]]));
      state.lc = (el as HTMLSelectElement).value;
      render();
    },

    async salvar(el) {
      const c = catBySlug(state.lc);
      const { faltam, payload } = validaCampos(c, lerCampos(c));
      if (faltam.length) {
        toast('Preencha: ' + faltam.join(', '));
        return;
      }
      el.classList.add('dis');
      try {
        const res = await api<ResultadoLancamento>('POST', '/lancamentos', { body: { categoria: c.slug, ...payload } });
        const total = res?.emissao ? num(res.emissao.emissao_total) : 0;
        // Limpa as quantidades para o próximo lançamento; mantém data, obra e listas.
        c.campos
          .filter((x) => !DOC_TOPO.includes(x.nome) && x.tipo !== 'opcoes' && !x.tipo.startsWith('lista:') && !temPadrao(x))
          .forEach((x) => {
            const input = document.getElementById('f_' + x.nome) as HTMLInputElement | null;
            if (input) input.value = '';
          });
        toast(`Lançamento gravado: ${nf(total, 3)} tCO₂e em ${obraNome(payload.id_unidade)}.`);
        carregarRecentes(state.tk);
      } catch (e) {
        toast((e as Error).message || 'Não foi possível salvar o lançamento.');
      } finally {
        el.classList.remove('dis');
      }
    },
  },
};
